import { Alert, Box } from "@mui/material"
import { GeoJSON as LGeoJSON, MapContainer, TileLayer } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import { bbox, geometryCollection } from "@turf/turf"
import { LatLngTuple } from "leaflet"
import { GeometryWithStatus, geometryReviewStatusProperties } from "../utils/GeoJSONUtils"

export interface ReviewedGeometriesMapProps {
    height: number
    geometries: GeometryWithStatus[]
}

const borderWidth = 2

/**
 * Note: This component doesn't re-render if the amount of geometries increases:
 * to avoid unexpected behavior when prop changes we need to add a key attribute when using this component
 * to show react it changed as a way to trigger that rerender. The suggested way is to use
 * key={JSON.stringify(__dependency__)}
 * so it only re-renders when that dependency changes.
 * This is because child components of React Leaflet are immutable unless stated
 * https://react-leaflet.js.org/docs/api-components/#props
 */
export const ReviewedGeometriesMap = ({ height, geometries }: ReviewedGeometriesMapProps) => {
    if (geometries.length === 0) {
        return <Alert severity="info">No map available</Alert>
    }

    const geometriesCollection = geometryCollection(geometries.map((geometry) => geometry.geometry))

    const mapBoundingBox = bbox(geometriesCollection)
    const leafletBounds: LatLngTuple[] = [
        [mapBoundingBox[1], mapBoundingBox[0]],
        [mapBoundingBox[3], mapBoundingBox[2]],
    ]

    return (
        <Box sx={{ border: borderWidth, borderColor: "#888" }}>
            <MapContainer
                bounds={leafletBounds}
                style={{
                    height,
                    width: "auto",
                }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {geometries.map((geometry, idx) => (
                    <LGeoJSON
                        key={idx}
                        data={geometry.geometry}
                        style={{
                            color: geometryReviewStatusProperties(geometry.status).color,
                            // Possibly in the future try to make this a glow or border? Hard when it is an SVG
                            weight: geometry.selected ? 4 : 2,
                        }}
                    />
                ))}
            </MapContainer>
        </Box>
    )
}
